import React from "react";
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import styled from "@emotion/styled";
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { selectTeamRefinances } from "../../../../../slices/teamStatsSlice";

const TeamStats = () => {
  const theme = useTheme();

  const teamRefinances = useSelector(selectTeamRefinances)

  const tooltipSx = {
    color: theme.palette.common.black,
    backgroundColor: theme.palette.common.white,
    border: `0.1rem solid ${theme.palette.common.grayBorder}`,
    boxShadow: '0 0.7rem 1.7rem rgba(15, 23, 42, 0.14)',
    borderRadius: '0.65rem',
    maxWidth: '17rem',
    padding: '0.75rem 0.85rem',
    fontSize: '0.78rem',
    fontWeight: 650,
    lineHeight: 1.4,
  }

  const formatNumber = (value) => value ? value.toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0}) : value

  const stats = teamRefinances !== null ? [
    {
      label: "Closed Refinances",
      value: teamRefinances.closedRefinances,
      detail: "Team total",
    },
    {
      label: "Gross Profit",
      value: formatNumber(teamRefinances.grossProfitNumber),
      prefix: "$",
      secondaryPrefix: "$",
      secondaryValue: formatNumber(teamRefinances.averageProfitNumber),
      detail: "Total and average",
      tooltip: "Value in parentheses is the average profit generated by all refinances.",
      emphasis: true,
    },
    {
      label: "Gross Revenue Increase",
      value: formatNumber(teamRefinances.grossProfitPercent),
      suffix: "%",
      secondaryValue: formatNumber(teamRefinances.averageProfitPercent),
      secondarySuffix: "%",
      detail: "Total and average",
      tooltip: "Value in parentheses is the average revenue increase generated by all refinances.",
    },
    {
      label: "Last Refinance",
      value: teamRefinances.lastRefinance ? teamRefinances.lastRefinance : `N/A`,
      detail: "Most recent close",
    },
    {
      label: "Refinance Frequency",
      value: teamRefinances.refinanceFrequency,
      unit: "days",
      detail: "Average cadence",
      tooltip: "Average days between the team closing a refinance.",
    },
  ] : []

  return (
    <StatsSurface>
      <StatsHeader>
        <div>
          <SectionEyebrow>Team Totals</SectionEyebrow>
          <SectionTitle>Team Stats</SectionTitle>
        </div>
        <SectionSummary>Closed refinance activity, profit contribution, and team cadence.</SectionSummary>
      </StatsHeader>
      {teamRefinances !== null ?
        <StatsGrid>
          {stats.map((stat) => (
            <StatCard key={stat.label} className={stat.emphasis ? "emphasis" : ""}>
              <StatLabelRow>
                <StatLabel>{stat.label}</StatLabel>
                {stat.tooltip &&
                  <Tooltip
                    componentsProps={{tooltip: {sx: tooltipSx}}}
                    PopperProps={{modifiers: [{
                      name: "offset",
                      options: {
                        offset: [0, -6],
                      },
                    }]}}
                    title={stat.tooltip}
                    placement="top"
                    arrow
                  >
                    <HelpButton aria-label={`${stat.label} context`} type="button">
                      <HelpOutlineIcon />
                    </HelpButton>
                  </Tooltip>
                }
              </StatLabelRow>
              <StatValue className={stat.emphasis ? "emphasis" : ""}>
                {stat.prefix && <Accent>{stat.prefix}</Accent>}
                {stat.value}
                {stat.suffix && <Accent>{stat.suffix}</Accent>}
                {stat.unit && <Unit>{stat.unit}</Unit>}
                {stat.secondaryValue !== undefined &&
                  <SecondaryValue>
                    ({stat.secondaryPrefix && <Accent>{stat.secondaryPrefix}</Accent>}
                    {stat.secondaryValue}
                    {stat.secondarySuffix && <Accent>{stat.secondarySuffix}</Accent>})
                  </SecondaryValue>
                }
              </StatValue>
              <StatDetail>{stat.detail}</StatDetail>
            </StatCard>
          ))}
        </StatsGrid>
      :
        <LoadingState>
          <CircularProgress
            sx={{ color: `${theme.palette.primary.main}`}}
            size={18}
          />
        </LoadingState>
      }
    </StatsSurface>
  )
}

const StatsSurface = styled.section(({ theme }) => `
  min-width: 0;
  display: grid;
  gap: 0;
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
`)

const StatsHeader = styled.header(({ theme }) => `
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: end;
  padding: 0.65rem 0.8rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const SectionEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.12rem;
  color: ${theme.palette.primary.main};
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const SectionTitle = styled.h2(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 0.94rem;
  font-weight: 850;
  line-height: 1.2;
`)

const SectionSummary = styled.p(({ theme }) => `
  max-width: 25rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.74rem;
  font-weight: 650;
  line-height: 1.25;
  text-align: right;
`)

const StatsGrid = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.55rem;
  padding: 0.62rem 0.75rem 0.72rem;
`

const StatCard = styled.article(({ theme }) => `
  min-width: 0;
  min-height: 5.55rem;
  display: grid;
  align-content: space-between;
  gap: 0.42rem;
  padding: 0.58rem 0.62rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.62rem;
  background: ${theme.palette.common.cardBackground};

  &.emphasis {
    border-color: ${theme.palette.primary.pastelMedium};
    background: ${theme.palette.common.white};
    box-shadow: inset 0 0.18rem 0 ${theme.palette.primary.labelTransparent};
  }
`)

const StatLabelRow = styled.div`
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.45rem;
`

const StatLabel = styled.span(({ theme }) => `
  min-width: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.74rem;
  font-weight: 850;
  line-height: 1.25;
`)

const HelpButton = styled.button(({ theme }) => `
  width: 1.35rem;
  height: 1.35rem;
  min-width: 1.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 999rem;
  background: transparent;
  color: ${theme.palette.common.grayText};
  cursor: help;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    color: ${theme.palette.primary.dark};
    background: ${theme.palette.primary.labelTransparent};
  }
`)

const StatValue = styled.div(({ theme }) => `
  min-width: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.18rem;
  color: ${theme.palette.common.black};
  font-size: 1.18rem;
  font-weight: 900;
  line-height: 1.08;
  letter-spacing: 0;

  &.emphasis {
    color: ${theme.palette.common.black};
    font-size: 1.48rem;
    font-weight: 900;
    letter-spacing: 0;
  }
`)

const Accent = styled.span(({ theme }) => `
  color: ${theme.palette.primary.pastelMedium};
  font-size: 0.9rem;
  font-weight: 850;
`)

const SecondaryValue = styled.span(({ theme }) => `
  margin-left: 0.16rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.78rem;
  font-weight: 800;
`)

const Unit = styled.span(({ theme }) => `
  margin-left: 0.1rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.84rem;
  font-weight: 800;
`)

const StatDetail = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.72rem;
  font-weight: 750;
  line-height: 1.2;
`)

const LoadingState = styled.div`
  min-width: 0;
  min-height: 6rem;
  display: grid;
  place-items: center;
`

export default TeamStats;
